// API Configuration
// Use relative path for Vite proxy, or full URL if VITE_API_URL is set
const API_URL = import.meta.env.VITE_API_URL 
  ? (import.meta.env.VITE_API_URL.endsWith('/api') 
      ? import.meta.env.VITE_API_URL 
      : `${import.meta.env.VITE_API_URL}/api`)
  : '/api'; // Use relative path for Vite proxy

// Import error tracker
import { errorTracker } from './errorTracker';

// Cookie-based authentication - no token management needed
// Cookies are automatically sent with requests when credentials: 'include' is set

// Clear auth data (for logout and errors)
export const clearAuthData = (): void => {
  localStorage.removeItem('orca_user');
  // Cookies are cleared by server on logout endpoint
};

// API request helper with cookie-based authentication
const apiRequest = async <T>(
  endpoint: string,
  options: RequestInit & { timeout?: number } = {}
): Promise<T> => {
  const headers: HeadersInit = {
    ...options.headers,
  };

  // Only set Content-Type for non-FormData requests
  // FormData requests need the browser to set Content-Type with boundary automatically
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  // No Authorization header needed - cookies sent automatically with credentials: 'include'

  console.log(`[API] ${options.method || 'GET'} ${API_URL}${endpoint}`, {
    hasBody: !!options.body,
    usingCookies: true
  });

  // Use custom timeout if provided, otherwise default based on endpoint complexity
  // NO TIMEOUT for audit - let it take as long as needed for large/complex websites
  const defaultTimeout = endpoint.includes('/audit') ? 900000 : // 15 minutes - NO TIMEOUT for audit, allow large websites to complete
                         endpoint.includes('/designmind-generate') ? 120000 : // 2 min - complex mockup generation
                         endpoint.includes('/competitor') ? 180000 : // 180s (3 min) - competitor analysis: 90s data extraction + 20s parallel AI + 60s final analysis = ~170s worst case
                         endpoint.includes('/analyze-gsc') ? 300000 : // 5 min - GSC analysis with file processing and AI (increased for large files)
                         (endpoint.includes('/seo-strategy') || endpoint.includes('/generate-article')) ? 60000 : // 60s - strategy and article generation
                         endpoint.includes('/optimize-content') || endpoint.includes('/content-brief') || endpoint.includes('/generate-faq') || endpoint.includes('/generate-headlines') ? 45000 : // 45s - content tools
                         endpoint.includes('/auth/login') || endpoint.includes('/auth/register') || endpoint.includes('/auth/resend-verification') ? 120000 : // 120s (2 min) for login/register/resend - increased for slow database connections
                         endpoint.includes('/credits') || endpoint.includes('/deduct') ? 30000 : // 30s for credit checks - increased for concurrent requests
                         endpoint.includes('/keywords') || endpoint.includes('/generate-meta') ? 30000 : // 30s - simpler tools
                         30000; // 30s default
  const timeout = options.timeout || defaultTimeout;
  
  // Create a fresh AbortController for each request to avoid signal conflicts
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  // Only set timeout if it's not infinite
  if (timeout > 0 && timeout < Infinity) {
    timeoutId = setTimeout(() => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    }, timeout);
  }

  let response: Response;
  try {
    // Don't pass signal if already aborted (prevents "signal is aborted without reason" error)
    let fetchOptions: RequestInit = {
      ...options,
      headers,
      credentials: 'include', // CRITICAL: Include cookies with all requests
    };
    
    // Only add signal if controller is not already aborted
    if (!controller.signal.aborted) {
      fetchOptions.signal = controller.signal;
    }
    
    response = await fetch(`${API_URL}${endpoint}`, fetchOptions);
    
    // Clear timeout on success
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  } catch (error: any) {
    // Clear timeout on error
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    
    // Track error
    errorTracker.trackError(error, {
      source: 'api',
      component: 'apiRequest',
      action: 'request-failed',
      context: {
        endpoint,
        method: options.method || 'GET',
        timeout,
        url: `${API_URL}${endpoint}`
      }
    });
    
    if (error.name === 'AbortError' || error.message?.includes('aborted') || error.message?.includes('signal is aborted')) {
      const isAuthEndpoint = endpoint.includes('/auth/login') || endpoint.includes('/auth/register');
      
      // Provide more helpful timeout messages based on endpoint
      const timeoutMessage = endpoint.includes('/competitor') 
        ? 'Competitor analysis timed out. The competitor website may be slow or complex. Please try again or use a simpler competitor page.'
        : endpoint.includes('/audit')
        ? 'Site audit timed out. The website may be too complex or slow. Please try again.'
        : isAuthEndpoint
        ? 'Login request timed out. The server may be busy or the database connection is slow. Please try again in a moment.'
        : 'Request timeout: The server took too long to respond. Please check if the backend is running.';
      
      // Only log non-auth timeouts to avoid spam
      if (!isAuthEndpoint) {
        errorTracker.trackWarning(`Request timeout: ${endpoint}`, {
          source: 'api',
          component: 'apiRequest',
          action: 'timeout',
          context: { endpoint, timeout, errorMessage: error.message }
        });
      }
      
      throw new Error(timeoutMessage);
    }
    // Network error (backend not running, CORS, etc.)
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.name === 'TypeError') {
      // Provide more helpful error message with troubleshooting steps
      const networkError = new Error(
        'Cannot connect to server. Please check:\n' +
        '1. Backend server is running (cd server && npm run dev)\n' +
        '2. Backend is running on http://localhost:3001\n' +
        '3. CORS is configured correctly\n' +
        '4. No firewall blocking the connection'
      );
      
      errorTracker.trackError(networkError, {
        source: 'api',
        component: 'apiRequest',
        action: 'network-error',
        context: {
          endpoint,
          method: options.method || 'GET',
          originalError: error.message,
          errorName: error.name,
          apiUrl: API_URL
        }
      });
      
      throw networkError;
    }
    throw error;
  }
  
  console.log(`[API] Response status: ${response.status} ${response.statusText}`);

  if (!response.ok) {
    // Read response body once
    let errorData: any = {};
    let errorMessage = `HTTP error! status: ${response.status}`;
    
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
        // Handle validation errors from express-validator
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const validationErrors = errorData.errors.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
          errorMessage = `Validation error: ${validationErrors}`;
        } else {
          errorMessage = errorData.error || errorData.message || errorMessage;
        }
      } else {
        const text = await response.text();
        errorMessage = text || errorMessage;
      }
    } catch (e) {
      // If we can't read the response, use default message
      console.warn('[API] Could not read error response:', e);
    }
    
    // Handle rate limiting errors
    if (response.status === 429) {
      const retryAfter = response.headers.get('Retry-After') || '15';
      errorMessage = `Too many requests. Please wait ${retryAfter} minutes before trying again.`;
      throw new Error(errorMessage);
    }
    
    // Handle authentication errors
    if (response.status === 401) {
      // Don't log expected 401 errors for /auth/me (checking if user is logged in)
      // This includes: no token, session expired, invalid token - all expected during initialization
      const isAuthCheck = endpoint === '/auth/me' && (
        errorMessage.includes('No token provided') ||
        errorMessage.includes('Session expired') ||
        errorMessage.includes('Invalid token') ||
        errorData.code === 'SESSION_EXPIRED' ||
        errorData.code === 'SESSION_INACTIVE'
      );
      
      // CRITICAL: Handle session conflict FIRST (single-session login enforcement)
      // This happens when user logs in from another browser/device
      if (errorMessage.includes('already logged in') || errorData.code === 'SESSION_CONFLICT') {
        // Immediately clear all auth data
        clearAuthData();
        // Dispatch a custom event so the app can react immediately
        window.dispatchEvent(new CustomEvent('session-conflict', { 
          detail: { message: 'Your account is logged in on another device or browser. Please log in again to continue.' }
        }));
        errorMessage = 'Your account is logged in on another device or browser. Please log in again to continue.';
      } else if (errorData.code === 'SESSION_INACTIVE') {
        // Session expired due to inactivity
        clearAuthData();
        errorMessage = 'Session expired due to inactivity. Please log in again.';
      } else if (!isAuthCheck && (errorMessage.includes('Session expired') || errorMessage.includes('Invalid token') || errorData.code === 'SESSION_EXPIRED')) {
        // Try to refresh token automatically (only for non-auth endpoints)
        try {
          const { authService } = await import('./authService.js');
          const refreshed = await authService.refreshToken();
          if (refreshed) {
            // Retry the original request with new token
            console.log('[API] Token refreshed, retrying request...');
            return apiRequest<T>(endpoint, options);
          }
        } catch (refreshError) {
          console.warn('[API] Token refresh failed:', refreshError);
          // Fall through to clear auth and show error
        }
        // If refresh failed or not applicable, clear auth
        clearAuthData();
        errorMessage = 'Session expired. Please log in again.';
      } else {
        // Other 401 errors
        clearAuthData();
        // Check if it's a "user not found" error
        if (errorMessage.includes('User not found')) {
          errorMessage = 'You are not yet registered. Please sign up now.';
        } else if (errorData.code === 'SESSION_EXPIRED') {
          errorMessage = 'Session expired. Please log in again.';
        } else {
          // Preserve more specific messages from backend
          if (!errorMessage || errorMessage.toLowerCase().includes('invalid token')) {
            errorMessage = 'Session expired. Please login again.';
          }
        }
      }
      
      // Only track error if it's not an expected auth check
      // Expected auth checks include: no token, session expired, invalid token for /auth/me endpoint
      if (!isAuthCheck) {
        // Track API error (but skip expected /auth/me checks)
        errorTracker.trackError(new Error(errorMessage), {
          source: 'api',
          component: 'apiRequest',
          action: 'http-error',
          context: {
            endpoint,
            method: options.method || 'GET',
            status: response.status,
            statusText: response.statusText,
            errorData
          }
        });
      }
      
      throw new Error(errorMessage);
    }
    
    // Handle database errors
    if (response.status === 503) {
      if (errorData.code === 'DATABASE_UNAVAILABLE' || errorData.code === 'DATABASE_TIMEOUT') {
        errorMessage = 'Database connection issue. Please check if MongoDB is running and try again.';
      }
    }
    
    // Handle approval errors
    if (response.status === 403 && errorMessage.includes('under review')) {
      // Don't clear token, just show the message
      errorMessage = 'Your account is under review. Please wait for admin approval.';
    }
    
    // Track API error (skip expected 401s for /auth/me when no token - already handled above)
    // Only track if it's not a 401 (401s are handled in the block above)
    if (response.status !== 401) {
      errorTracker.trackError(new Error(errorMessage), {
        source: 'api',
        component: 'apiRequest',
        action: 'http-error',
        context: {
          endpoint,
          method: options.method || 'GET',
          status: response.status,
          statusText: response.statusText,
          errorData
        }
      });
    }
    
    throw new Error(errorMessage);
  }

  // Parse JSON response
  try {
    const contentType = response.headers.get('content-type');
    const text = await response.text();
    
    // Check if response is empty
    if (!text || text.trim().length === 0) {
      throw new Error('Server returned empty response. Please try again.');
    }
    
    // Try to parse as JSON
    if (contentType && contentType.includes('application/json')) {
      try {
        return JSON.parse(text);
      } catch (jsonError: any) {
        console.error('[API] JSON parse error:', jsonError.message);
        console.error('[API] Response preview:', text.substring(0, 500));
        throw new Error(`Failed to parse AI response. The AI may have returned invalid JSON. Please try again.`);
      }
    } else {
      // Try to parse as JSON anyway (some servers don't set content-type correctly)
      if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
        try {
          return JSON.parse(text);
        } catch (jsonError: any) {
          console.error('[API] JSON parse error (no content-type):', jsonError.message);
          console.error('[API] Response preview:', text.substring(0, 500));
          throw new Error(`Failed to parse AI response. The AI may have returned invalid JSON. Please try again.`);
        }
      }
      throw new Error(`Unexpected response format. Expected JSON but got: ${contentType || 'unknown'}`);
    }
  } catch (parseError: any) {
    // Re-throw our custom error messages
    if (parseError.message && parseError.message.includes('Failed to parse AI response')) {
      throw parseError;
    }
    if (parseError.message && parseError.message.includes('Unexpected token')) {
      throw new Error('Failed to parse AI response. The AI may have returned invalid JSON. Please try again.');
    }
    throw parseError;
  }
};

// Booking API functions
export interface BookingAvailability {
  availableSlots: string[];
}

export interface BookingCreateRequest {
  consultingType: 'seo' | 'wordpress' | 'woocommerce';
  duration: 30 | 60 | 90;
  date: string;
  startTime: string;
  clientInfo: {
    fullName: string;
    email: string;
    telegram: string;
    phone?: string;
    website?: string;
  };
  consultationDetails: {
    topic: string;
    legalConfirmation: boolean;
    termsAccepted: boolean;
  };
}

export interface BookingCreateResponse {
  bookingId: string;
  paypalOrderId: string;
  approvalUrl?: string;
}

export interface Booking {
  bookingId: string;
  consultingType: 'seo' | 'wordpress' | 'woocommerce';
  duration: number;
  price: number;
  date: string;
  startTime: string;
  endTime: string;
  clientInfo: {
    fullName: string;
    email: string;
    telegram: string;
    phone?: string;
    website?: string;
  };
  consultationDetails: {
    topic: string;
    legalConfirmation: boolean;
    termsAccepted: boolean;
  };
  payment: {
    status: 'pending' | 'completed' | 'failed' | 'refunded';
    amount: number;
    currency: string;
    paypalTransactionId?: string;
    paidAt?: string;
  };
  googleMeet: {
    meetingLink?: string;
    eventId?: string;
  };
  status: 'confirmed' | 'completed' | 'cancelled' | 'no-show';
  cancellationReason?: string;
  cancelledAt?: string;
  notifications: {
    telegramSent: boolean;
    confirmationEmailSent: boolean;
    reminderEmailSent: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

export const bookingService = {
  // Get available time slots
  getAvailability: async (date: string, duration: 30 | 60 | 90): Promise<BookingAvailability> => {
    return apiRequest<BookingAvailability>(
      `/bookings/availability?date=${encodeURIComponent(date)}&duration=${duration}`,
      { method: 'GET' }
    );
  },

  // Create booking and get PayPal approval URL
  createBooking: async (data: BookingCreateRequest): Promise<BookingCreateResponse> => {
    return apiRequest<BookingCreateResponse>(
      '/bookings/create',
      {
        method: 'POST',
        body: JSON.stringify(data)
      }
    );
  },

  // Verify PayPal payment
  verifyPayment: async (bookingId: string, paypalOrderId: string): Promise<{ success: boolean; booking: any }> => {
    return apiRequest<{ success: boolean; booking: any }>(
      '/bookings/verify-payment',
      {
        method: 'POST',
        body: JSON.stringify({ bookingId, paypalOrderId })
      }
    );
  },

  // Get user's bookings
  getMyBookings: async (): Promise<{ bookings: Booking[] }> => {
    return apiRequest<{ bookings: Booking[] }>(
      '/bookings/my-bookings',
      { method: 'GET' }
    );
  },

  // Get all bookings (admin only)
  getAllBookings: async (filters?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    consultingType?: string;
  }): Promise<{ bookings: Booking[] }> => {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.consultingType) params.append('consultingType', filters.consultingType);
    
    const queryString = params.toString();
    return apiRequest<{ bookings: Booking[] }>(
      `/bookings/all${queryString ? '?' + queryString : ''}`,
      { method: 'GET' }
    );
  },

  // Cancel booking
  cancelBooking: async (bookingId: string, reason?: string): Promise<{ success: boolean; refundAmount: number; refundStatus: string }> => {
    return apiRequest<{ success: boolean; refundAmount: number; refundStatus: string }>(
      '/bookings/cancel',
      {
        method: 'POST',
        body: JSON.stringify({ bookingId, reason })
      }
    );
  }
};

export default apiRequest;
